import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useFormattedNumber } from '../hooks/useFormattedNumber';
import BaseModal from './common/BaseModal';
import { useAuth } from '../contexts/AuthContext';
import './AddScenarioModal.css'; // Import custom CSS for styling

const AddScenarioModal = ({ isOpen, onClose, houseId }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isCashPurchase, setIsCashPurchase] = useState(false);
  const [rentalType, setRentalType] = useState('long-term'); // 'long-term' or 'short-term'
  const [userSettings, setUserSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Regular form fields
  const [name, setName] = useState('');
  const [downPaymentPercent, setDownPaymentPercent] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTermYears, setLoanTermYears] = useState('');

  // Formatted number fields
  const price = useFormattedNumber('');
  const closingCosts = useFormattedNumber('0');
  const monthlyRent = useFormattedNumber('');
  const nightlyRate = useFormattedNumber('');
  const [occupancyRate, setOccupancyRate] = useState('65'); // Default to 65% occupancy

  // Check if required fields are filled
  const isFormValid = () => {
    const basicFieldsValid = name !== '' && price.value !== '';
    
    if (isCashPurchase) {
      return basicFieldsValid;
    } else {
      return basicFieldsValid && 
             downPaymentPercent !== '' && 
             interestRate !== '' && 
             loanTermYears !== '';
    }
  };

  // Fetch user settings when the modal opens
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (isOpen && currentUser?.uid) {
        try {
          setLoadingSettings(true);
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserSettings({
              // Long-term rental settings
              defaultManagementFee: userData.defaultManagementFee || '',
              defaultVacancyRate: userData.defaultVacancyRate || '',
              defaultMaintenanceRate: userData.defaultMaintenanceRate || '',
              // Short-term rental settings
              defaultShortTermManagementFee: userData.defaultShortTermManagementFee || '',
              defaultOccupancyRate: userData.defaultOccupancyRate || '65',
              defaultShortTermMaintenanceRate: userData.defaultShortTermMaintenanceRate || ''
            });
            
            // Set occupancy rate from user settings if available
            if (userData.defaultOccupancyRate) {
              setOccupancyRate(userData.defaultOccupancyRate);
            }
          } else {
            setUserSettings(null);
          }
        } catch (error) {
          console.error('Error fetching user settings:', error);
          setUserSettings(null);
        } finally {
          setLoadingSettings(false);
        }
      }
    };

    fetchUserSettings();
  }, [isOpen, currentUser]);
  
  // Handle modal overflow
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Calculate mortgage payment and cash to close
      let monthlyMortgage = 0;
      let cashToClose = 0;
      
      if (isCashPurchase) {
        // For cash purchase, no mortgage payment
        monthlyMortgage = 0;
        cashToClose = parseFloat(price.value) + parseFloat(closingCosts.value || 0);
      } else {
        // For financed purchase, calculate mortgage
        const principal = parseFloat(price.value) * (1 - parseFloat(downPaymentPercent) / 100);
        const monthlyRate = (parseFloat(interestRate) / 100) / 12;
        const numberOfPayments = parseInt(loanTermYears) * 12;
        
        // Use the standard amortizing loan formula
        monthlyMortgage = principal * 
          (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
          (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
        
        // Calculate cash to close
        cashToClose = (parseFloat(price.value) * (parseFloat(downPaymentPercent) / 100)) + 
                      parseFloat(closingCosts.value || 0);
      }

      // Calculate yearly values based on rental type
      let yearlyGrossRent = 0;
      if (rentalType === 'long-term') {
        // For long-term rentals, simply multiply monthly rent by 12
        yearlyGrossRent = parseFloat(monthlyRent.value || 0) * 12;
      } else {
        // For short-term rentals, calculate based on nightly rate and user-defined occupancy rate
        const nightlyRateValue = parseFloat(nightlyRate.value || 0);
        const occupancyRateDecimal = parseFloat(occupancyRate) / 100;
        const estimatedOccupancyDays = 365 * occupancyRateDecimal;
        yearlyGrossRent = nightlyRateValue * estimatedOccupancyDays;
      }
      
      const yearlyMortgage = monthlyMortgage * 12;

      // Initialize expense rates from user settings if available
      let managementFeePercent = 0;
      let vacancyRatePercent = 0;
      let maintenanceRatePercent = 0;
      
      // Apply default expense percentages from user settings based on rental type
      if (userSettings) {
        console.log(`Applying default ${rentalType} expense percentages from user settings`);
        
        if (rentalType === 'long-term') {
          // Use long-term rental expense settings
          managementFeePercent = userSettings.defaultManagementFee ? parseFloat(userSettings.defaultManagementFee) : 0;
          vacancyRatePercent = userSettings.defaultVacancyRate ? parseFloat(userSettings.defaultVacancyRate) : 0;
          maintenanceRatePercent = userSettings.defaultMaintenanceRate ? parseFloat(userSettings.defaultMaintenanceRate) : 0;
        } else {
          // Use short-term rental expense settings
          managementFeePercent = userSettings.defaultShortTermManagementFee ? parseFloat(userSettings.defaultShortTermManagementFee) : 0;
          // For short-term rentals, we don't use vacancy rate (it's replaced by occupancy rate)
          vacancyRatePercent = 0;
          maintenanceRatePercent = userSettings.defaultShortTermMaintenanceRate ? parseFloat(userSettings.defaultShortTermMaintenanceRate) : 0;
        }
      }
      
      // Calculate yearly expense values
      const yearlyInsurance = 0; // No default for insurance
      const yearlyTaxes = 0; // No default for property taxes
      const yearlyManagement = yearlyGrossRent * (managementFeePercent / 100);
      const yearlyVacancy = yearlyGrossRent * (vacancyRatePercent / 100);
      const yearlyMaintenance = yearlyGrossRent * (maintenanceRatePercent / 100); 

      // Calculate total expenses (excluding mortgage)
      const totalExpenses = yearlyInsurance + yearlyTaxes + yearlyManagement + yearlyVacancy + yearlyMaintenance;

      // Calculate NOI (Net Operating Income)
      const noi = yearlyGrossRent - totalExpenses;
      
      // Calculate cash flow
      const cashFlow = noi - yearlyMortgage;
      
      // Calculate DSCR (Debt Service Coverage Ratio)
      const dscr = isCashPurchase ? null : (yearlyMortgage > 0 ? noi / yearlyMortgage : 0);

      // Calculate cash on cash return
      const cashOnCashReturn = cashToClose > 0 ? (cashFlow / cashToClose) * 100 : 0;

      // Calculate cap rate
      const capRate = parseFloat(price.value) > 0 ? (noi / parseFloat(price.value)) * 100 : 0;

      // Calculate principal paydown for year 1 (only for financed purchases)
      let principalPaydownPercent = 0;
      
      if (!isCashPurchase) {
        // Only calculate principal paydown for regular amortizing loans
        let remainingBalance = parseFloat(price.value) * (1 - parseFloat(downPaymentPercent) / 100);
        const startOfYearBalance = remainingBalance;
        const monthlyRate = (parseFloat(interestRate || 0) / 100) / 12;
        
        for (let month = 1; month <= 12; month++) {
          const interestPayment = remainingBalance * monthlyRate;
          const principalPayment = monthlyMortgage - interestPayment;
          remainingBalance = remainingBalance - principalPayment;
        }
        
        const yearlyPrincipalPaydown = startOfYearBalance - remainingBalance;
        principalPaydownPercent = cashToClose > 0 ? (yearlyPrincipalPaydown / cashToClose) * 100 : 0;
      }

      // No default appreciation rate
      const appreciationRate = 0; 
      const yearlyAppreciationAmount = parseFloat(price.value) * (appreciationRate / 100);
      const appreciationPercent = cashToClose > 0 ? (yearlyAppreciationAmount / cashToClose) * 100 : 0;

      // Calculate total ROI
      const totalROI = cashOnCashReturn + principalPaydownPercent + appreciationPercent;

      // Create the new scenario document
      const scenarioData = {
        houseId,
        name,
        price: parseFloat(price.value),
        isCashPurchase,
        downPaymentPercent: isCashPurchase ? 100 : parseFloat(downPaymentPercent),
        interestRate: isCashPurchase ? 0 : parseFloat(interestRate),
        loanTermYears: isCashPurchase ? 0 : parseInt(loanTermYears),
        closingCosts: parseFloat(closingCosts.value || 0),
        rentalType: rentalType,
        // Always store both monthly rent and nightly rate
        monthlyRent: parseFloat(monthlyRent.value || 0),
        nightlyRate: parseFloat(nightlyRate.value || 0),
        // Always store occupancy rate in its own field
        occupancyRate: parseFloat(occupancyRate || 65),
        cashToClose,
        // Add expense values with defaults from user settings
        monthlyInsurance: 0,
        propertyTax: 0,
        managementFee: managementFeePercent, 
        vacancyRate: vacancyRatePercent,   
        maintenanceRate: maintenanceRatePercent, 
        appreciation: 0,  
        rentGrowth: 0, 
        calculations: {
          // Metrics
          capRate: capRate || 0,
          annualCashFlow: cashFlow || 0,
          annualCashOnCashReturn: cashOnCashReturn || 0,
          dscr: dscr || 0,
          annualROI: totalROI || 0,
          
          // Annual values
          annualGrossRent: yearlyGrossRent || 0,
          annualExpenses: 0,
          annualInsurance: 0,
          annualMaintenance: 0,
          annualManagement: 0,
          annualMortgage: yearlyMortgage * 12 || 0,
          annualTaxes: 0,
          annualVacancy: 0
        },
        // Add empty placeholder arrays for chart projections
        // These will be properly calculated and updated when the scenario view loads
        chartProjections: {
          cashOnCash: Array(10).fill(0),
          totalROI: Array(10).fill(0)
        },
        houseId,
        userId: currentUser.uid, // Add the current user's ID for permissions
        createdBy: currentUser.uid, // For backward compatibility
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      try {
        const docRef = await addDoc(collection(db, 'scenarios'), scenarioData);
        navigate(`/scenario/${docRef.id}`);
      } catch (error) {
        console.error('Error creating scenario:', error);
        alert(`Failed to create scenario: ${error.message}. Please try again.`);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error adding scenario:", error);
      setIsLoading(false);
    }
  };

  const modalFooter = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="btn btn-primary"
        disabled={isLoading || !isFormValid()}
        onClick={handleSubmit}
      >
        {isLoading ? 'Adding...' : 'Add Scenario'}
      </button>
    </>
  );

  // CSS for required field asterisk
  const requiredFieldStyle = {
    color: 'red',
    marginLeft: '3px'
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Scenario"
      footer={modalFooter}
      maxWidth="900px"
      width="70%"
    >
      <form className="needs-validation" noValidate onKeyDown={(e) => {
          if (e.key === 'Enter' && isFormValid() && !isLoading) {
            e.preventDefault();
            handleSubmit(e);
          }
        }}>
        <div className="mb-3">
          <label className="form-label">
            Scenario Name
            <span style={requiredFieldStyle}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter scenario name"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Rental Type</label>
          <div className="d-flex">
            <div className="form-check me-4">
              <input
                className="form-check-input"
                type="radio"
                name="rentalType"
                id="longTerm"
                value="long-term"
                checked={rentalType === 'long-term'}
                onChange={() => setRentalType('long-term')}
              />
              <label className="form-check-label text-muted" htmlFor="longTerm">
                Long-term Rental
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="rentalType"
                id="shortTerm"
                value="short-term"
                checked={rentalType === 'short-term'}
                onChange={() => setRentalType('short-term')}
              />
              <label className="form-check-label text-muted" htmlFor="shortTerm">
                Short-term Rental
              </label>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">
            Purchase Price
            <span style={requiredFieldStyle}>*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="text"
              className="form-control"
              value={price.formatted}
              onChange={price.onChange}
              placeholder="Enter purchase price"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Purchase Method</label>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="cashPurchaseToggle"
              checked={isCashPurchase}
              onChange={() => {
                setIsCashPurchase(!isCashPurchase);
                if (!isCashPurchase) {
                  // When enabling cash purchase, set down payment to 100%
                  setDownPaymentPercent('100');
                }
              }}
            />
            <label className="form-check-label text-muted" htmlFor="cashPurchaseToggle">
              Cash Purchase
            </label>
          </div>
        </div>

        {!isCashPurchase && (
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">
                Down Payment %
                <span style={requiredFieldStyle}>*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={downPaymentPercent}
                  onChange={(e) => setDownPaymentPercent(e.target.value)}
                  placeholder="20"
                  min="0"
                  max="100"
                />
                <span className="input-group-text">%</span>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">
                Interest Rate %
                <span style={requiredFieldStyle}>*</span>
              </label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={interestRate}
                  onChange={(e) => setInterestRate(e.target.value)}
                  placeholder="6.5"
                  step="0.1"
                  min="0"
                />
                <span className="input-group-text">%</span>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">
                Loan Term (Years)
                <span style={requiredFieldStyle}>*</span>
              </label>
              <input
                type="number"
                className="form-control"
                value={loanTermYears}
                onChange={(e) => setLoanTermYears(e.target.value)}
                placeholder="30"
                min="1"
              />
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Closing Costs</label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="text"
              className="form-control"
              value={closingCosts.formatted}
              onChange={closingCosts.onChange}
              placeholder="Enter closing costs"
            />
          </div>
        </div>

        {/* Show Monthly Rent for long-term rentals */}
        {rentalType === 'long-term' && (
          <div className="mb-3">
            <label className="form-label">Monthly Rent</label>
            <div className="input-group">
              <span className="input-group-text">$</span>
              <input
                type="text"
                className="form-control"
                value={monthlyRent.formatted}
                onChange={monthlyRent.onChange}
                placeholder="Enter expected monthly rent"
              />
            </div>
          </div>
        )}

        {/* Show Nightly Rate and Occupancy Rate for short-term rentals in a row */}
        {rentalType === 'short-term' && (
          <div className="mb-3">
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Nightly Rate</label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <input
                    type="text"
                    className="form-control"
                    value={nightlyRate.formatted}
                    onChange={nightlyRate.onChange}
                    placeholder="Enter expected nightly rate"
                  />
                </div>
              </div>
              
              <div className="col-md-6">
                <label className="form-label">Occupancy Rate (%)</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={occupancyRate}
                    onChange={(e) => {
                      const value = e.target.value.replace(/%/g, '');
                      setOccupancyRate(value);
                    }}
                    placeholder="Enter occupancy rate"
                  />
                  <span className="input-group-text">%</span>
                </div>
              </div>
            </div>
            <small className="text-muted">
              Note: For short-term rentals, enter the average nightly rate. The system will calculate income based on this rate and occupancy.
            </small>
          </div>
        )}
      </form>
    </BaseModal>
  );
};

export default AddScenarioModal;
